"use client";

import React from "react";
import NextLink from "next/link";
import { Icon, type IconName } from "./Icon";

/**
 * Botón unificado de la plataforma.
 *
 * No introduce estilos propios: emite las MISMAS clases del botón de Webflow
 * (`button`, `is-transparent`, `is-text`, `size-s`, y las variantes de color
 * `w-variant-…`) que ya pinta el CSS global del sitio, de modo que el resultado
 * es idéntico a los componentes DevLink `ButtonSolid(S)`/`ButtonTransparent(S)`/
 * `ButtonText(S)`. Aquí vive una sola definición, con semántica de `<button>`
 * real (submit/onClick/disabled) y soporte de `<Icon>` (currentColor).
 *
 * Ejes del sistema de diseño (espejo de la maqueta):
 *  - `variant`      → forma: solid / transparent / text.
 *  - `settingsStyle`→ color/estado: dark / light / success / error / warning /
 *                     disabled. Es el eje "oficial" del design system.
 *  - `size`         → m (default) / s.
 *
 * Compatibilidad: `tone` (default/negative/positive/offBlack) es el eje legacy
 * previo (clases `is-negative`/`is-positive`/`is-off-black`). Se mantiene para no
 * romper las pantallas existentes; cuando se pasa `settingsStyle` (o el botón
 * queda deshabilitado) ese eje toma precedencia sobre `tone`.
 *
 * Cuando se pasa `href` renderiza un `NextLink` (navegación); en cualquier otro
 * caso, un `<button>`.
 *
 * ## Semántica de color (regla del producto, no del CSS)
 *
 * El color NO se elige por "qué tan positiva se siente" la acción, sino por lo
 * que pasa en el momento del clic:
 *
 * - **Rojo (`settingsStyle="error"`)** — solo acciones destructivas o
 *   irreversibles: eliminar, borrar, cancelar una invitación, restablecer un
 *   diseño, salir perdiendo cambios.
 * - **Negro (default, `settingsStyle="dark"`)** — la acción principal de la
 *   pantalla: guardar, crear, continuar, confirmar. Es el default: si el usuario
 *   solo necesita saber cuál botón lo hace avanzar, va negro.
 * - **Verde (`settingsStyle="success"`)** — reservado como capa extra de
 *   advertencia para cuando la acción **sale del sistema o se vuelve pública**:
 *   enviar invitaciones, publicar el micrositio, mandar un recordatorio o correo
 *   masivo, confirmar un cambio que el invitado ya va a ver.
 *
 * La pregunta al elegir: *¿en el momento en que se aprieta, le manda algo a
 * alguien fuera del sistema (invitado, tercero) o lo hace público?* Si sí →
 * verde. Si no —aunque sea "guardar algo nuevo y positivo"— → negro. Guardar un
 * borrador, crear una sección, cambiar la URL de la mesa o subir una foto a la
 * galería son **negro**.
 *
 * Ojo con el eje legacy: `tone="negative"` pinta `.button.is-negative` (relleno
 * rojo sólido) incluso en `variant="transparent"`. Para un secundario rojo usa
 * `variant="transparent" settingsStyle="error"`.
 *
 * ## Jerarquía por forma (pies de modal y de cajón)
 *
 * La forma dice cuánto peso tiene la acción, y el color qué tipo de acción es.
 * En un pie con varias acciones solo hay UN sólido:
 *
 * - **Primario → `variant="solid"`**: la acción de la pantalla (Guardar, Enviar).
 * - **Secundario → `variant="transparent"`**: la alternativa (Cancelar; o el
 *   destructivo cuando el primario es otro).
 * - **Terciario → `variant="text"`**: la acción de menor peso (Eliminar en el
 *   pie de un cajón de edición, o Cancelar cuando ya hay tres botones).
 *
 * Con tres acciones, ejemplo del cajón "Editar invitado": Guardar sólido negro,
 * Cancelar transparente, Eliminar texto rojo (`variant="text"
 * settingsStyle="error"`). `ModalContentAlert` ya aplica esta degradación solo.
 */

/** Forma del botón (clases base de Webflow). */
export type ButtonVariant =
  | "solid" // primario relleno (button w-button)
  | "transparent" // secundario contorneado (button is-transparent)
  | "text"; // tipo enlace (button is-text)

/** Color/estado del botón (variantes `settingsStyle` de la maqueta). */
export type ButtonStyle =
  | "dark"
  | "light"
  | "success"
  | "error"
  | "warning"
  | "disabled";

/** Tamaño del botón (m = default, s = compacto `size-s`). */
export type ButtonSize = "m" | "s";

/**
 * Tratamiento del texto en botones `variant="text"`, espejo del `textType` de la
 * maqueta. Sin definir, el texto se renderiza plano (comportamiento previo). Con
 * `"accent"`/`"body"` se envuelve en `.text-block` como en el DevLink `ButtonText`
 * (accent = `font-family-accent text-caps-all`).
 */
export type ButtonTextStyle = "accent" | "body";

/** Variante del `.text-block` para el tamaño `s` de los botones de texto. */
const TEXT_BLOCK_S_VARIANT = "w-variant-706cbeb4-3681-3ad5-4094-af352e0de9cb";

/** Eje de color legacy (previo a `settingsStyle`). Se conserva por compat. */
export type ButtonTone =
  | "default"
  | "negative" // peligro/eliminar (is-negative)
  | "positive" // confirmar (is-positive)
  | "offBlack"; // sólido off-black (is-off-black)

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  solid: "",
  transparent: "is-transparent",
  text: "is-text",
};

const TONE_CLASS: Record<ButtonTone, string> = {
  default: "",
  negative: "is-negative",
  positive: "is-positive",
  offBlack: "is-off-black",
};

/**
 * Matriz de variantes de color de Webflow, indexada por forma → tamaño → estado.
 * Los `w-variant-…` salen 1:1 de los componentes DevLink (fuente de verdad);
 * `dark` es la base (sin clase). Cambiar aquí obliga a re-sincronizar contra la
 * maqueta si Webflow regenera los IDs.
 */
const BUTTON_STYLE_CLASS: Record<
  ButtonVariant,
  Record<ButtonSize, Record<ButtonStyle, string>>
> = {
  solid: {
    m: {
      dark: "",
      light: "w-variant-48ab0a06-5b10-7d3a-bea9-0e7390154b82",
      success: "w-variant-fe51c53b-4929-a98e-d215-61a2ae688e05",
      error: "w-variant-23398939-8a5a-bc68-9f17-383ee36557a7",
      warning: "w-variant-85753bcf-12f5-68b3-95d8-e89214897edf",
      disabled: "w-variant-4685955f-ee5d-920b-0124-fdeca64b2db2",
    },
    s: {
      dark: "",
      light: "w-variant-39917cb5-1928-3ddd-c566-c46869a1cf2d",
      success: "w-variant-f43fb4d6-95ad-2598-827f-b6d3fb784951",
      error: "w-variant-a659d906-b6af-9dfe-d6e9-f850be83d312",
      warning: "w-variant-15fc4ff7-fc46-b663-7eaa-10a5cbd04b27",
      disabled: "w-variant-a85be05a-1114-c786-5e32-2dbab0e939a3",
    },
  },
  transparent: {
    m: {
      dark: "",
      light: "w-variant-7ababda9-6a56-0fbe-73a5-ae14879483ff",
      success: "w-variant-d7e29c58-c366-d4e5-f135-9d95e4dfb416",
      error: "w-variant-8dffd16b-45c5-b41e-fe53-737c4dae8fe2",
      warning: "w-variant-a429c279-40f9-64fc-2ade-6b43aa75b2cc",
      disabled: "w-variant-704a2cfd-d4e8-65d2-6580-799e5dab9f89",
    },
    s: {
      dark: "",
      light: "w-variant-a448fb52-45f6-03f3-3cdd-5f70ebddee20",
      success: "w-variant-852abf63-113a-1df8-3b42-48d3f570b4ad",
      error: "w-variant-6e4cfbd3-82a4-a9c7-2d70-b964a1f7142b",
      warning: "w-variant-ab62000c-4d29-767b-9835-965131fb4fa6",
      disabled: "w-variant-46bf31b6-ac61-522d-f46f-c3795761a48d",
    },
  },
  text: {
    m: {
      dark: "",
      light: "w-variant-a0458118-bbf2-b684-9db6-f96aaa4625ff",
      success: "w-variant-26717129-59a6-2500-e4c5-f006daf0691f",
      error: "w-variant-bc47f29f-d35d-4669-1b16-cbb4b97a59c9",
      warning: "w-variant-fff5155c-ab93-43ee-5318-9b468f8bc28d",
      disabled: "w-variant-8fe1670e-fec8-ab9f-ea91-031850770168",
    },
    s: {
      dark: "",
      light: "w-variant-b159504c-a042-24c1-0565-cfd597c16065",
      success: "w-variant-c777908f-4633-45f5-8c13-46332572eded",
      error: "w-variant-a31473c0-893d-af40-d605-6496d5214843",
      warning: "w-variant-fb6086fa-ee40-e113-c7fc-2ed338731d93",
      disabled: "w-variant-d2027817-4eca-cf8b-9c67-56c36064be38",
    },
  },
};

/** Atenuado transitorio mientras una acción está en curso (`loading`). */
const LOADING_STYLE: React.CSSProperties = {
  opacity: 0.3,
  cursor: "not-allowed",
};

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type CommonProps = {
  variant?: ButtonVariant;
  /** Color/estado del design system. Toma precedencia sobre `tone`. */
  settingsStyle?: ButtonStyle;
  /** Eje de color legacy. Ignorado si se pasa `settingsStyle`. */
  tone?: ButtonTone;
  /** `"s"` añade la clase `size-s`; `"m"` (default) es el tamaño base. */
  size?: ButtonSize;
  /**
   * Solo para `variant="text"`: envuelve el texto en `.text-block` (accent/body),
   * como el DevLink `ButtonText`. Sin definir, el texto va plano.
   */
  textStyle?: ButtonTextStyle;
  /**
   * Ícono del botón. Acepta un nombre del registro (`<Icon>`) o un nodo SVG
   * propio. Se pinta en la caja `.button-icon` de la maqueta, del mismo lado
   * que indique `iconPosition`.
   */
  icon?: IconName | React.ReactElement;
  /**
   * Lado del ícono, espejo del `iconPosition` de los botones de DevLink
   * (`true` = izquierda, `false` = derecha). Default: izquierda.
   *
   * Excepción con regla propia: si el botón **abre un menú** (dropdown, select,
   * cualquier trigger con `aria-haspopup="menu"`), la flecha o chevron va
   * SIEMPRE a la derecha —`iconPosition="right"`—, después del texto. A la
   * izquierda se lee como ícono de la acción, no como "esto despliega".
   */
  iconPosition?: "left" | "right";
  /**
   * Segundo ícono, siempre a la derecha. Solo para los botones que llevan uno
   * de cada lado; para uno solo a la derecha, usa `iconPosition="right"`.
   */
  iconRight?: IconName | React.ReactElement;
  /**
   * Botón cuadrado de solo-ícono (estilo `is-cart` de Webflow): compacto y con
   * `aspect-ratio: 1`. Pensado para acciones de fila (enviar, eliminar). Ignora
   * `size`, `settingsStyle` y el texto; usa `icon` para el glifo.
   */
  iconOnly?: boolean;
  /** Deshabilita el botón y lo atenúa mientras una acción está en curso. */
  loading?: boolean;
  /** Clases extra que se anexan a las de Webflow. */
  className?: string;
  children?: React.ReactNode;
};

type ButtonAsButton = CommonProps &
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> & {
    href?: undefined;
  };

type ButtonAsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "className" | "href"> & {
    href: string;
    target?: "_self" | "_blank";
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

export function Button(props: ButtonProps) {
  const {
    variant = "solid",
    settingsStyle,
    tone = "default",
    size = "m",
    textStyle,
    icon,
    iconPosition = "left",
    iconRight,
    iconOnly = false,
    loading = false,
    className,
    children,
    style,
    ...rest
  } = props;

  // Un `<button>` con `disabled` nativo debe verse deshabilitado aunque no se
  // pase `settingsStyle`. Los links no tienen `disabled` nativo. `loading` es
  // transitorio: se atenúa aparte y no cambia el color de la variante.
  const isDisabledButton =
    !("href" in props && props.href !== undefined) &&
    Boolean((rest as React.ButtonHTMLAttributes<HTMLButtonElement>).disabled);

  // Estado de color efectivo: `settingsStyle` explícito gana; si no, un botón
  // deshabilitado usa la variante "disabled"; en otro caso, se cede al eje
  // legacy `tone`.
  const effectiveStyle: ButtonStyle | undefined =
    settingsStyle ?? (isDisabledButton ? "disabled" : undefined);

  const classes = cx(
    "button",
    // `is-cart` es el botón cuadrado de solo-ícono de Webflow (compacto). Define
    // su propio padding/tamaño, así que en ese modo no se aplican `size-s`,
    // `w-button` ni las variantes de color.
    iconOnly && "is-cart",
    VARIANT_CLASS[variant],
    // El eje legacy solo aplica cuando no hay estado de color del design system.
    !effectiveStyle && TONE_CLASS[tone],
    !iconOnly && size === "s" && "size-s",
    // `w-button` es el reset de botón de Webflow; los rellenos lo llevan, los de
    // tipo enlace (`is-text`) no.
    !iconOnly && variant !== "text" && "w-button",
    !iconOnly && effectiveStyle && BUTTON_STYLE_CLASS[variant][size][effectiveStyle],
    className
  );

  const mergedStyle: React.CSSProperties = {
    ...(loading ? LOADING_STYLE : null),
    ...style,
  };

  // Misma estructura que los botones de la maqueta: la caja `.button-icon`
  // (0.875rem de ancho, contenido centrado) que el `.button` separa del texto
  // con su propio gap. En DevLink el lado lo decide `iconPosition` (true =
  // izquierda) y el hueco del glifo va vacío porque viene del CMS; aquí el
  // glifo se pinta de verdad.
  const renderIcon = (ic?: IconName | React.ReactElement) =>
    ic == null ? null : (
      <div className="button-icon">
        {typeof ic === "string" ? (
          // `.button-icon` fija el ancho pero no el alto: el glifo lleva su
          // propio tamaño, más chico en los botones `size-s`.
          <Icon name={ic} size={size === "s" ? 12 : 14} />
        ) : (
          ic
        )}
      </div>
    );

  // Para `variant="text"` con `textStyle`, el texto va en un `.text-block` (con
  // la variante de tamaño y, si es accent, `font-family-accent text-caps-all`),
  // igual que el DevLink `ButtonText`. Sin `textStyle`, el texto va plano.
  const textNode =
    variant === "text" && textStyle ? (
      <div
        className={cx(
          "text-block",
          size === "s" && TEXT_BLOCK_S_VARIANT,
          textStyle === "accent" && "font-family-accent text-caps-all"
        )}
      >
        {children}
      </div>
    ) : (
      children
    );

  // En modo solo-ícono el `.button.is-cart` centra el glifo por sí mismo, sin la
  // caja `.button-icon` ni texto.
  const content = iconOnly ? (
    typeof icon === "string" ? <Icon name={icon} size={14} /> : icon
  ) : (
    <>
      {iconPosition === "left" ? renderIcon(icon) : null}
      {textNode}
      {iconPosition === "right" ? renderIcon(icon) : null}
      {renderIcon(iconRight)}
    </>
  );

  if ("href" in props && props.href !== undefined) {
    // `_href` solo se desestructura para sacarlo del spread: el destino lo pone
    // `props.href` unas líneas abajo.
    const { href: _href, ...anchorRest } =
      rest as React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };
    return (
      <NextLink
        href={props.href}
        className={classes}
        style={mergedStyle}
        {...anchorRest}
      >
        {content}
      </NextLink>
    );
  }

  const buttonRest = rest as React.ButtonHTMLAttributes<HTMLButtonElement>;
  return (
    <button
      className={classes}
      style={mergedStyle}
      // El default de HTML para `<button>` es `submit`, así que un botón sin
      // `type` dentro de un `<form>` lo envía: en la pantalla de cuenta el
      // botón "cancelar" acababa guardando lo que el usuario quería descartar.
      // Aquí el default se invierte —como en la mayoría de design systems— y
      // enviar pasa a ser explícito. Va ANTES del spread a propósito: un
      // `type="submit"` del llamador tiene que seguir ganando.
      type="button"
      {...buttonRest}
      disabled={loading || buttonRest.disabled}
    >
      {content}
    </button>
  );
}
